import React, { useEffect, useCallback } from "react";
import { Layout } from "@/components/Layout";
import {
  useGetExtractionStatus,
  useBatchExtract,
  useRetryExtraction,
  useRetryAllErrors,
  getGetExtractionStatusQueryKey,
  getGetExtractionStatusQueryOptions,
  useListRecipes,
} from "@workspace/api-client-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DUPLICATES_COUNT_QUERY_KEY } from "@/lib/queryKeys";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Play,
  RotateCw,
  AlertTriangle,
  FileText,
  CheckCircle2,
  Clock,
  Images,
  RefreshCcw,
  Zap,
  Copy,
  ChevronRight,
} from "lucide-react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { BatchReextractSection } from "@/components/BatchReextractSection";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const PIE_COLORS = [
  "#6366f1", "#ec4899", "#f59e0b", "#10b981", "#3b82f6",
  "#8b5cf6", "#ef4444", "#14b8a6", "#f97316", "#84cc16",
];

const STATUS_COLORS: Record<string, string> = {
  validated: "#10b981",
  needs_correction: "#f97316",
  unreviewed: "#94a3b8",
};

const SOURCE_COLORS: Record<string, string> = {
  handwritten: "#6366f1",
  printed: "#3b82f6",
  mixed: "#8b5cf6",
  digital: "#06b6d4",
};

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getBaseUrl(): string {
  return import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
}

interface NutritionBatchStatus {
  running: boolean;
  total: number;
  done: number;
  errors: number;
  remaining: number;
  withEstimates: number;
}

export default function DashboardPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [retryingIds, setRetryingIds] = React.useState<Set<number>>(new Set());
  const [selectedFailedIds, setSelectedFailedIds] = React.useState<Set<number>>(new Set());
  const [reextractingSelected, setReextractingSelected] = React.useState(false);
  const [nutritionStatus, setNutritionStatus] = React.useState<NutritionBatchStatus | null>(null);
  const [nutritionPolling, setNutritionPolling] = React.useState(false);
  const [nutritionStarting, setNutritionStarting] = React.useState(false);

  const { data: status, isLoading } = useGetExtractionStatus({
    query: { ...getGetExtractionStatusQueryOptions(), refetchInterval: 3000 },
  });

  const { data: recipesData } = useListRecipes();
  const allRecipes = recipesData?.recipes ?? [];

  const batchExtractMut = useBatchExtract({
    mutation: {
      onSuccess: (data) => {
        toast({
          title: "Batch queued",
          description:
            data.queued > 0
              ? `${data.queued} images added to the extraction queue.`
              : "All pending images are already queued.",
        });
        queryClient.invalidateQueries({ queryKey: getGetExtractionStatusQueryKey() });
      },
    },
  });

  const retryAllMut = useRetryAllErrors({
    mutation: {
      onSuccess: (data) => {
        toast({
          title: "Retry queued",
          description:
            data.queued > 0
              ? `${data.queued} images re-queued for extraction.`
              : "No errors to retry.",
        });
        queryClient.invalidateQueries({ queryKey: getGetExtractionStatusQueryKey() });
      },
    },
  });

  const retryMut = useRetryExtraction();

  const handleStartBatch = () => batchExtractMut.mutate();
  const handleRetryAll = () => retryAllMut.mutate();

  const fetchNutritionStatus = useCallback(async () => {
    try {
      const r = await fetch(`${getBaseUrl()}/api/nutrition/estimate/status`);
      if (r.ok) {
        const data = (await r.json()) as NutritionBatchStatus;
        setNutritionStatus(data);
        if (!data.running) setNutritionPolling(false);
      }
    } catch {
      setNutritionPolling(false);
    }
  }, []);

  useEffect(() => {
    fetchNutritionStatus();
  }, [fetchNutritionStatus]);

  // Pending-duplicates count for the dashboard banner. Uses react-query
  // so it refetches on mount AND on window focus (overriding the global
  // refetchOnWindowFocus: false default), and so the duplicates page can
  // invalidate the same key after dismiss / merge / delete / scan to
  // keep the banner from going stale.
  const { data: duplicatesCountData } = useQuery({
    queryKey: DUPLICATES_COUNT_QUERY_KEY,
    queryFn: async () => {
      const r = await fetch(`${getBaseUrl()}/api/duplicates/count`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return (await r.json()) as { pending: number };
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0,
  });
  const duplicatesPending = duplicatesCountData?.pending ?? null;

  useEffect(() => {
    if (!nutritionPolling) return;
    const id = setInterval(fetchNutritionStatus, 2000);
    return () => clearInterval(id);
  }, [nutritionPolling, fetchNutritionStatus]);

  const handleStartNutritionBatch = async () => {
    setNutritionStarting(true);
    try {
      const r = await fetch(`${getBaseUrl()}/api/nutrition/estimate/batch`, { method: "POST" });
      const data = await r.json() as { queued?: number; error?: string };
      if (!r.ok) {
        toast({
          title: "Cannot start nutrition estimates",
          description: data.error ?? "Unknown error",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Nutrition estimates started",
        description: `Estimating ${data.queued} recipes using USDA database + AI.`,
      });
      setNutritionPolling(true);
      fetchNutritionStatus();
    } catch {
      toast({ title: "Request failed", variant: "destructive" });
    } finally {
      setNutritionStarting(false);
    }
  };

  const toggleSelectFailed = (id: number) => {
    setSelectedFailedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleReextractSelected = async (mode: "overwrite" | "candidate") => {
    const imageIds = Array.from(selectedFailedIds);
    if (imageIds.length === 0) {
      toast({
        title: "No images selected",
        description: "Pick at least one image to re-extract.",
        variant: "destructive",
      });
      return;
    }
    setReextractingSelected(true);
    try {
      const r = await fetch(`${getBaseUrl()}/api/reextract`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode,
          filter: { type: "selected", imageIds },
        }),
      });
      const data = (await r.json()) as {
        queued?: number;
        alreadyInFlight?: number;
        failed?: number;
        total?: number;
        error?: string;
      };
      if (!r.ok) {
        toast({
          title: "Re-extract failed",
          description: data.error ?? "Unknown error",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: mode === "candidate" ? "Candidates queued" : "Re-extraction queued",
        description: `${data.queued ?? 0} queued · ${data.alreadyInFlight ?? 0} already running · ${data.failed ?? 0} failed`,
      });
      setSelectedFailedIds(new Set());
      queryClient.invalidateQueries({ queryKey: getGetExtractionStatusQueryKey() });
    } catch {
      toast({ title: "Request failed", variant: "destructive" });
    } finally {
      setReextractingSelected(false);
    }
  };

  const handleRetry = (id: number) => {
    setRetryingIds((prev) => new Set(prev).add(id));
    retryMut.mutate(
      { imageId: id },
      {
        onSuccess: () => {
          toast({ title: "Retry complete", description: "Image re-extracted successfully." });
          queryClient.invalidateQueries({ queryKey: getGetExtractionStatusQueryKey() });
        },
        onError: () => {
          toast({
            title: "Retry failed",
            description: "Could not re-extract image.",
            variant: "destructive",
          });
        },
        onSettled: () => {
          setRetryingIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        },
      }
    );
  };

  if (isLoading && !status) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RotateCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const s = status || {
    pending: 0,
    processing: 0,
    extracted: 0,
    validated: 0,
    error: 0,
    total: 0,
    totalRecipes: 0,
    errors: [],
  };

  const completed = s.extracted + s.validated;
  const progressPercent = s.total > 0 ? Math.round((completed / s.total) * 100) : 0;
  const isProcessing = s.processing > 0;

  // ── Recipe analytics ──────────────────────────────────────────────────────
  const categoryMap: Record<string, number> = {};
  const sourceTypeMap: Record<string, number> = {};
  const statusMap: Record<string, number> = { validated: 0, needs_correction: 0, unreviewed: 0 };

  for (const r of allRecipes) {
    const cat = r.category || "Uncategorized";
    categoryMap[cat] = (categoryMap[cat] ?? 0) + 1;

    const src = r.sourceType || "Unknown";
    sourceTypeMap[src] = (sourceTypeMap[src] ?? 0) + 1;

    const st = r.validationStatus || "unreviewed";
    statusMap[st] = (statusMap[st] ?? 0) + 1;
  }

  const categoryData = Object.entries(categoryMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const sourceTypeData = Object.entries(sourceTypeMap)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const validatedCount = statusMap.validated ?? 0;
  const validationPct =
    allRecipes.length > 0
      ? Math.round((validatedCount / allRecipes.length) * 100)
      : 0;

  // Recent activity: last 10 validated recipes by updatedAt
  const recentActivity = [...allRecipes]
    .filter((r) => r.validationStatus === "validated" && r.updatedAt)
    .sort(
      (a, b) =>
        new Date(b.updatedAt!).getTime() - new Date(a.updatedAt!).getTime()
    )
    .slice(0, 10);

  return (
    <Layout>
      <div className="space-y-8">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-serif font-bold text-foreground">
              Extraction Dashboard
            </h2>
            <p className="text-muted-foreground mt-1">
              Monitor Claude AI processing status
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            {s.error > 0 && (
              <Button
                size="lg"
                variant="outline"
                onClick={handleRetryAll}
                disabled={isProcessing || s.pending > 0 || retryAllMut.isPending}
                className="gap-2 border-destructive/40 text-destructive hover:bg-destructive/5"
              >
                {retryAllMut.isPending ? (
                  <RotateCw className="w-5 h-5 animate-spin" />
                ) : (
                  <RefreshCcw className="w-5 h-5" />
                )}
                Retry All ({s.error}) Errors
              </Button>
            )}
            <Button
              size="lg"
              onClick={handleStartBatch}
              disabled={s.pending === 0 || isProcessing || batchExtractMut.isPending}
              className="gap-2"
            >
              {isProcessing ? (
                <RotateCw className="w-5 h-5 animate-spin" />
              ) : (
                <Play className="w-5 h-5" />
              )}
              {isProcessing ? "Processing Batch..." : "Start Batch Extraction"}
            </Button>
          </div>
        </div>

        {/* ── Image Progress ──────────────────────────────────────────────── */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex justify-between items-end mb-2">
              <div>
                <h3 className="text-sm font-medium text-primary mb-1">
                  Overall Progress
                </h3>
                <div className="text-2xl font-bold text-foreground">
                  {completed} / {s.total}{" "}
                  <span className="text-base font-normal text-muted-foreground">
                    Images Processed
                  </span>
                </div>
              </div>
              <div className="text-primary font-bold text-xl">{progressPercent}%</div>
            </div>
            <Progress value={progressPercent} className="h-4 bg-primary/10" />
          </CardContent>
        </Card>

        {/* ── Stats Grid ─────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title="Total Images"
            value={s.total}
            icon={<Images className="w-5 h-5 text-muted-foreground" />}
          />
          <StatCard
            title="Total Recipes"
            value={s.totalRecipes}
            icon={<FileText className="w-5 h-5 text-primary" />}
          />
          <StatCard
            title="Pending Review"
            value={s.extracted}
            icon={<Clock className="w-5 h-5 text-warning" />}
          />
          <StatCard
            title="Validated"
            value={s.validated}
            icon={<CheckCircle2 className="w-5 h-5 text-success" />}
          />
          <StatCard
            title="Errors"
            value={s.error}
            icon={<AlertTriangle className="w-5 h-5 text-destructive" />}
            alert={s.error > 0}
          />
        </div>

        {/* ── Duplicate Review Banner ─────────────────────────────────────
            Only render once we have a real count (skip the loading flicker).
            Shown even when zero so admins can confirm "all clear" rather
            than wonder if the system stopped checking. */}
        {duplicatesPending !== null && (
          <Link
            href="/admin/duplicates"
            className={`block rounded-2xl border p-4 transition-colors hover:shadow-sm ${
              duplicatesPending > 0
                ? "bg-amber-50/70 border-amber-200/70 hover:bg-amber-50"
                : "bg-muted/30 border-border hover:bg-muted/50"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div
                  className={`p-2.5 rounded-xl shadow-sm border ${
                    duplicatesPending > 0
                      ? "bg-white border-amber-200"
                      : "bg-white border-border/50"
                  }`}
                >
                  <Copy
                    className={`w-5 h-5 ${
                      duplicatesPending > 0 ? "text-amber-600" : "text-muted-foreground"
                    }`}
                  />
                </div>
                <div>
                  <p className="font-medium text-foreground flex items-center gap-2">
                    Duplicate Review
                    {duplicatesPending > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-amber-500 text-white text-xs font-bold">
                        {duplicatesPending}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {duplicatesPending > 0
                      ? `${duplicatesPending} potential duplicate ${
                          duplicatesPending === 1 ? "pair" : "pairs"
                        } waiting for review`
                      : "No pending duplicates — your cookbook is clean."}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </Link>
        )}

        {/* ── Nutrition Batch Section ─────────────────────────────────────── */}
        <Card className="bg-orange-50/60 border-orange-200/60">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-orange-500" />
                <CardTitle className="text-base font-semibold">Nutrition Estimates</CardTitle>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleStartNutritionBatch}
                disabled={
                  nutritionStarting ||
                  (nutritionStatus?.running ?? false) ||
                  (nutritionStatus?.remaining === 0 && (nutritionStatus?.withEstimates ?? 0) > 0)
                }
                className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-100"
              >
                {nutritionStatus?.running || nutritionStarting ? (
                  <RotateCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4" />
                )}
                {nutritionStatus?.running
                  ? "Estimating…"
                  : nutritionStarting
                  ? "Starting…"
                  : nutritionStatus?.remaining === 0 && (nutritionStatus?.withEstimates ?? 0) > 0
                  ? "All Estimated"
                  : "Estimate All Recipes"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {nutritionStatus ? (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {nutritionStatus.withEstimates} of {nutritionStatus.total} recipes estimated
                  </span>
                  {nutritionStatus.running && (
                    <span className="text-orange-600 font-medium text-xs">
                      {nutritionStatus.done} done · {nutritionStatus.errors} errors · {nutritionStatus.remaining} remaining
                    </span>
                  )}
                </div>
                <Progress
                  value={nutritionStatus.total > 0 ? Math.round((nutritionStatus.withEstimates / nutritionStatus.total) * 100) : 0}
                  className="h-2 bg-orange-100"
                />
                <p className="text-xs text-muted-foreground">
                  Uses USDA FoodData Central database + AI estimates. Results appear on each recipe's Validate page and in the Recipes library.
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Loading status…</p>
            )}
          </CardContent>
        </Card>

        {/* ── Batch Re-Extraction ─────────────────────────────────────────── */}
        <BatchReextractSection />

        {/* ── Errors Section ─────────────────────────────────────────────── */}
        {s.errors && s.errors.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-xl font-serif font-bold flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" /> Needs Attention
              </h3>
              <span className="text-sm text-muted-foreground">
                {s.errors.length} image{s.errors.length !== 1 ? "s" : ""} failed
                {selectedFailedIds.size > 0 && (
                  <> · {selectedFailedIds.size} selected</>
                )}
              </span>
            </div>

            {/* Hand-pick bulk action bar — uses the `selected` filter on
                the re-extract API to push exactly the chosen image ids
                through the queue. Visible only once at least one image
                is checked so it doesn't clutter the page in the common
                case. Candidate mode is intentionally NOT offered here:
                every image in this list failed extraction, so none of
                them have a linked recipe to compare against, and the
                backend rejects candidate jobs without a recipe. The
                "Overwrite" path falls back to a fresh extraction and
                creates a new recipe, which is what an admin actually
                wants for failed images. */}
            {selectedFailedIds.size > 0 && (
              <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">
                    {selectedFailedIds.size} image
                    {selectedFailedIds.size !== 1 ? "s" : ""} selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedFailedIds(new Set())}
                    disabled={reextractingSelected}
                  >
                    Clear
                  </Button>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleReextractSelected("overwrite")}
                  disabled={reextractingSelected}
                >
                  {reextractingSelected ? (
                    <RotateCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCcw className="w-4 h-4 mr-2" />
                  )}
                  Re-extract Selected
                </Button>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-border overflow-hidden">
              {/* Select-all header row. Only shown when there's more than
                  one error so single-failure cases don't get a redundant
                  control. */}
              {s.errors.length > 1 && (
                <div className="px-4 py-2 border-b border-border bg-muted/30 flex items-center gap-3">
                  <input
                    type="checkbox"
                    aria-label="Select all failed images"
                    className="h-4 w-4 rounded border-border accent-primary cursor-pointer"
                    checked={
                      selectedFailedIds.size === s.errors.length &&
                      s.errors.length > 0
                    }
                    ref={(el) => {
                      if (el) {
                        el.indeterminate =
                          selectedFailedIds.size > 0 &&
                          selectedFailedIds.size < s.errors.length;
                      }
                    }}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedFailedIds(
                          new Set(s.errors.map((err) => err.id))
                        );
                      } else {
                        setSelectedFailedIds(new Set());
                      }
                    }}
                  />
                  <span className="text-xs text-muted-foreground">
                    Select all to re-extract in bulk
                  </span>
                </div>
              )}

              <div className="divide-y divide-border">
                {s.errors.map((err) => {
                  const isRetrying = retryingIds.has(err.id);
                  const isSelected = selectedFailedIds.has(err.id);
                  return (
                    <div
                      key={err.id}
                      className={`p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-colors ${
                        isSelected ? "bg-primary/5" : "hover:bg-muted/30"
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <input
                          type="checkbox"
                          aria-label={`Select ${err.originalName} for re-extraction`}
                          className="mt-6 h-4 w-4 rounded border-border accent-primary cursor-pointer"
                          checked={isSelected}
                          onChange={() => toggleSelectFailed(err.id)}
                        />
                        <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden shrink-0 border border-border/50">
                          <img
                            src={`/api/images/file/${err.filename}`}
                            alt={err.originalName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src =
                                "https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=100&h=100&fit=crop";
                            }}
                          />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{err.originalName}</p>
                          <p className="text-sm text-destructive mt-1 line-clamp-2 max-w-2xl">
                            {err.errorMessage || "Unknown extraction error"}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => handleRetry(err.id)}
                        disabled={isRetrying}
                        className="shrink-0"
                      >
                        <RotateCw
                          className={`w-4 h-4 mr-2 ${isRetrying ? "animate-spin" : ""}`}
                        />
                        {isRetrying ? "Retrying..." : "Retry"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Recipe Analytics ───────────────────────────────────────────── */}
        {allRecipes.length > 0 && (
          <>
            <div className="pt-4 border-t border-border/50">
              <h3 className="text-2xl font-serif font-bold text-foreground mb-1">
                Recipe Analytics
              </h3>
              <p className="text-muted-foreground text-sm">
                Insights across your {allRecipes.length}-recipe collection
              </p>
            </div>

            {/* Validation progress */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-end mb-2">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">
                      Validation Completion
                    </h4>
                    <div className="text-xl font-bold text-foreground">
                      {validatedCount}{" "}
                      <span className="text-sm font-normal text-muted-foreground">
                        / {allRecipes.length} recipes validated
                      </span>
                    </div>
                  </div>
                  <div className="text-green-600 font-bold text-xl">{validationPct}%</div>
                </div>
                <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all"
                    style={{ width: `${validationPct}%` }}
                  />
                </div>
                <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    Validated: {statusMap.validated ?? 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-orange-400" />
                    Needs correction: {statusMap.needs_correction ?? 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-slate-400" />
                    Unreviewed: {statusMap.unreviewed ?? 0}
                  </span>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Category pie chart */}
              {categoryData.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold">
                      Recipes by Category
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {categoryData.map((_, idx) => (
                            <Cell
                              key={`cell-${idx}`}
                              fill={PIE_COLORS[idx % PIE_COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [value, name]}
                          contentStyle={{
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                        />
                        <Legend
                          iconType="circle"
                          iconSize={8}
                          formatter={(value) => (
                            <span style={{ fontSize: 12 }}>{value}</span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Source type bar chart */}
              {sourceTypeData.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold">
                      Recipes by Source Type
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart
                        data={sourceTypeData}
                        margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                      >
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 12 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                          width={28}
                        />
                        <Tooltip
                          contentStyle={{ borderRadius: 8, fontSize: 12 }}
                          cursor={{ fill: "hsl(var(--muted))" }}
                        />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={60}>
                          {sourceTypeData.map((entry, idx) => (
                            <Cell
                              key={`bar-${idx}`}
                              fill={
                                SOURCE_COLORS[entry.name.toLowerCase()] ??
                                PIE_COLORS[idx % PIE_COLORS.length]
                              }
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Recent activity */}
            {recentActivity.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold">
                    Recently Validated
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {recentActivity.map((r) => (
                      <div
                        key={r.id}
                        className="px-6 py-3 flex items-center justify-between gap-4 hover:bg-muted/30 transition-colors"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-foreground truncate">
                            {r.title}
                          </p>
                          {r.category && (
                            <p className="text-xs text-muted-foreground">{r.category}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(r.updatedAt)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function StatCard({
  title,
  value,
  icon,
  alert,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
  alert?: boolean;
}) {
  return (
    <Card className={alert ? "border-destructive/30 bg-destructive/5" : ""}>
      <CardContent className="p-6 flex items-center gap-4">
        <div className="p-3 bg-white rounded-xl shadow-sm border border-border/50">
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold text-foreground">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
